// Tile Types

export type TileMetric =
  | 'total_companies'
  | 'total_pages'
  | 'companies_with_addons'
  | 'companies_with_multiple_pages'
  | 'companies_with_public_pricing'
  | 'companies_with_freemium'
  | 'companies_with_trial'
  | 'companies_with_contact_pricing'
  | 'pages_with_addons'
  | 'pages_with_license_pricing'
  | 'pages_with_usage_pricing'
  | 'pages_with_credit_pricing'
  | 'pages_with_thresholds'
  | 'avg_plans_per_page';

export interface TileConfig {
  metric: TileMetric;
  label?: string; // Custom label, falls back to default metric label
}

export interface TileRowConfig {
  tiles: TileConfig[]; // Array of tile configs (max 5)
  // Note: period and filters come from the report's SetDefinition, not stored here
}

export interface TileRowElementContent {
  config: TileRowConfig;
}

export interface TileMetricMetadata {
  label: string; // Short label for dropdown (within grouped context)
  displayLabel: string; // Full label for tile display (standalone context)
  description: string;
  format: 'number' | 'percentage' | 'decimal';
}

export const TILE_METRIC_METADATA: Record<TileMetric, TileMetricMetadata> = {
  total_companies: {
    label: 'Total',
    displayLabel: 'Total Companies',
    description: 'Total number of companies matching filters',
    format: 'number'
  },
  total_pages: {
    label: 'Total',
    displayLabel: 'Total Pages',
    description: 'Total number of pricing pages',
    format: 'number'
  },
  companies_with_addons: {
    label: 'With Add-ons',
    displayLabel: 'Companies with Add-ons',
    description: 'Companies offering add-ons',
    format: 'number'
  },
  companies_with_multiple_pages: {
    label: 'With Multiple Pages',
    displayLabel: 'Companies with Multiple Pages',
    description: 'Companies with multiple pricing pages',
    format: 'number'
  },
  companies_with_public_pricing: {
    label: 'With Public Pricing',
    displayLabel: 'Companies with Public Pricing',
    description: 'Companies with publicly displayed pricing',
    format: 'number'
  },
  companies_with_freemium: {
    label: 'With Freemium',
    displayLabel: 'Companies with Freemium',
    description: 'Companies offering a freemium plan',
    format: 'number'
  },
  companies_with_trial: {
    label: 'With Trial',
    displayLabel: 'Companies with Trial',
    description: 'Companies offering a free trial',
    format: 'number'
  },
  companies_with_contact_pricing: {
    label: 'With Contact Pricing',
    displayLabel: 'Companies with Contact Pricing',
    description: 'Companies requiring contact for pricing',
    format: 'number'
  },
  pages_with_addons: {
    label: 'With Add-ons',
    displayLabel: 'Pages with Add-ons',
    description: 'Pricing pages with add-ons',
    format: 'number'
  },
  pages_with_license_pricing: {
    label: 'With License Pricing',
    displayLabel: 'Pages with License Pricing',
    description: 'Pages with license-based pricing',
    format: 'number'
  },
  pages_with_usage_pricing: {
    label: 'With Usage Pricing',
    displayLabel: 'Pages with Usage Pricing',
    description: 'Pages with usage-based pricing',
    format: 'number'
  },
  pages_with_credit_pricing: {
    label: 'With Credit Pricing',
    displayLabel: 'Pages with Credit Pricing',
    description: 'Pages with credit-based pricing',
    format: 'number'
  },
  pages_with_thresholds: {
    label: 'With Thresholds',
    displayLabel: 'Pages with Thresholds',
    description: 'Pages with pricing thresholds',
    format: 'number'
  },
  avg_plans_per_page: {
    label: 'Avg Plans per Page',
    displayLabel: 'Avg Plans per Page',
    description: 'Average number of pricing plans per page',
    format: 'decimal'
  }
};
